import React, { useMemo, useState } from "react";
import { apiTreeLog, FOLLOWUP_METHODS, FOLLOWUP_STATUS_UPDATES } from "./api";
import { CustomIcon } from "../shared/CustomIcon";

// Log Follow-Up modal — records a follow-up against a node and (optionally)
// updates the member's status. Phase 5.
export default function LogFollowUpModal({ node, member, onClose, onLogged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [statusUpdate, setStatusUpdate] = useState("No Change");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const canSave = useMemo(() => !!date && !!method && !saving, [date, method, saving]);

  if (!node) return null;

  const displayName = member ? `${member.firstName || ""} ${member.lastName || ""}`.trim() : "Vacant";
  const initials = member ? ((member.firstName?.[0] || "?") + (member.lastName?.[0] || "")) : "?";

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr(null);
    try {
      const res = await apiTreeLog({
        nodeId: node.nodeId,
        date, method, notes,
        statusUpdate: statusUpdate === "No Change" ? null : statusUpdate,
      });
      onLogged && onLogged(res);
      onClose && onClose();
    } catch (e) {
      setErr(e?.message || "Failed to log follow-up.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="log-followup-modal"
      onClick={onClose}
      role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 260, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mobile-safe-bottom-pad"
        style={{
          background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", borderRadius: 14,
          width: "min(520px, 100%)", maxHeight: "85vh", overflowY: "auto",
          padding: "20px 20px calc(24px + env(safe-area-inset-bottom)) 20px",
          boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          {member?.photo ? (
            <img src={member.photo} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div data-testid="lfm-name" style={{ fontFamily: "'Cinzel',serif", fontSize: 17, fontWeight: 700, color: "var(--kc-text)" }}>{displayName}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)" }}>
              {node.levelName || "—"} · #{node.memberNumber || node.slotRef}
            </div>
          </div>
          <button aria-label="Close" data-testid="lfm-close" onClick={onClose} style={{ background: "none", border: "none", color: "var(--kc-sub)", cursor: "pointer", fontSize: 22, flexShrink: 0 }}>×</button>
        </div>

        {/* Fields */}
        <label style={fieldLabel}>Date</label>
        <input
          type="date" data-testid="lfm-date" value={date}
          onChange={(e) => setDate(e.target.value)} max={today}
          style={fieldInput}
        />

        <label style={fieldLabel}>Follow-up method</label>
        <select
          data-testid="lfm-method" value={method}
          onChange={(e) => setMethod(e.target.value)} style={fieldInput}
        >
          <option value="">Select…</option>
          {FOLLOWUP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <label style={fieldLabel}>Notes (optional)</label>
        <textarea
          data-testid="lfm-notes" value={notes}
          onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What did you discuss? Prayer requests, follow-up actions…"
          style={{ ...fieldInput, resize: "vertical", minHeight: 68 }}
        />

        <label style={fieldLabel}>Status update</label>
        <select
          data-testid="lfm-status" value={statusUpdate}
          onChange={(e) => setStatusUpdate(e.target.value)} style={fieldInput}
        >
          {FOLLOWUP_STATUS_UPDATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {statusUpdate !== "No Change" && (
          <div style={{ fontSize: 11, color: "#f59e0b", marginTop: -2, marginBottom: 8, fontFamily: "'Montserrat',sans-serif" }}>
            <CustomIcon name="warning" size={11} /> This will change {displayName}&apos;s member status to <strong>{statusUpdate}</strong>.
          </div>
        )}

        {err && <div data-testid="lfm-error" style={{ color: "#ff6b6b", fontSize: 12, margin: "6px 0" }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" data-testid="lfm-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-gold btn-sm" data-testid="lfm-save" onClick={save} disabled={!canSave}>
            {saving ? "Saving…" : "Save Follow-Up"}
          </button>
        </div>
      </div>
    </div>
  );
}

const fieldLabel = {
  display: "block", fontSize: 11, color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)",
  fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
  letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 10, marginBottom: 5,
};
const fieldInput = {
  width: "100%", background: "#111", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
  color: "var(--kc-text)", padding: "9px 10px", borderRadius: 8, fontSize: 13,
  fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box",
};
